import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Loader2, CalendarDays, AlertTriangle, RefreshCw } from "lucide-react";

const IMPACTS = [
  { v: "high", label: "High" },
  { v: "medium", label: "Medium" },
  { v: "all", label: "All" },
];

const impactMeta = (imp) => {
  const i = (imp || "").toLowerCase();
  if (i === "high") return { c: "text-loss", bg: "bg-loss/12 border-loss/30", dot: "#FF4757", label: "High" };
  if (i === "medium") return { c: "text-amber", bg: "bg-amber/12 border-amber/30", dot: "#F59E0B", label: "Medium" };
  if (i === "holiday") return { c: "text-brand", bg: "bg-brand/12 border-brand/25", dot: "#2962FF", label: "Holiday" };
  return { c: "text-slate-400", bg: "bg-white/5 border-white/10", dot: "#64748B", label: imp || "Low" };
};

function dayKey(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
  } catch { return "Upcoming"; }
}
function timeOf(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}
function isToday(iso) {
  try {
    const d = new Date(iso); const n = new Date();
    return d.toDateString() === n.toDateString();
  } catch { return false; }
}

export const EconomicCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impact, setImpact] = useState("high");
  const [err, setErr] = useState("");

  const load = useCallback(async (imp) => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/calendar/economic?impact=${imp}`);
      setEvents(data.events || []);
    } catch (e) {
      setErr("Could not load the economic calendar. Please try again.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(impact); }, [impact, load]);

  const groups = events.reduce((acc, ev) => {
    const k = dayKey(ev.date);
    (acc[k] = acc[k] || []).push(ev);
    return acc;
  }, {});

  return (
    <div data-testid="economic-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-sm text-slate-400 max-w-xl">
          High-impact economic releases for the week — plan around the data that moves the markets.
        </p>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-[#0C1017] border border-white/10 rounded-lg p-1">
            {IMPACTS.map((f) => (
              <button
                key={f.v}
                data-testid={`calendar-filter-${f.v}`}
                onClick={() => setImpact(f.v)}
                className={`px-3 py-1.5 rounded-md font-data text-[11px] uppercase tracking-wider transition-colors ${
                  impact === f.v ? "bg-brand text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button
            data-testid="calendar-refresh-btn"
            onClick={() => load(impact)}
            className="h-9 w-9 grid place-items-center rounded-md border border-white/10 text-slate-400 hover:text-brand hover:border-brand/40 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-24 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
      ) : err ? (
        <div className="card-surface rounded-xl py-16 flex flex-col items-center text-center">
          <AlertTriangle className="h-8 w-8 text-amber mb-3" />
          <p className="text-sm text-slate-400 max-w-sm">{err}</p>
        </div>
      ) : events.length === 0 ? (
        <div className="card-surface rounded-xl py-16 flex flex-col items-center text-center">
          <CalendarDays className="h-8 w-8 text-slate-500 mb-3" />
          <p className="text-sm text-slate-400 max-w-sm">No {impact !== "all" ? impact : ""} impact events scheduled for the rest of this week.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groups).map(([day, list]) => (
            <div key={day} data-testid="calendar-day-group">
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="h-4 w-4 text-brand" />
                <h3 className="font-heading text-sm font-bold text-slate-100">{day}</h3>
                {isToday(list[0].date) && (
                  <span className="font-data text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gain/12 text-gain border border-gain/25">Today</span>
                )}
                <span className="font-data text-[11px] text-slate-600">· {list.length}</span>
              </div>
              <div className="card-surface rounded-xl overflow-hidden divide-y divide-white/5">
                {list.map((ev, i) => {
                  const m = impactMeta(ev.impact);
                  return (
                    <motion.div
                      key={i}
                      data-testid="calendar-event-row"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i, 8) * 0.03 }}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors"
                    >
                      <span className="font-data text-xs text-slate-400 w-16 shrink-0">{timeOf(ev.date)}</span>
                      <span className="font-data text-[10px] font-bold w-11 shrink-0 text-center px-1.5 py-1 rounded bg-white/5 border border-white/10 text-slate-200">{ev.country}</span>
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: m.dot }} title={m.label} />
                      <span className="flex-1 text-sm text-slate-200 truncate">{ev.title}</span>
                      <div className="hidden sm:flex items-center gap-4 shrink-0">
                        {ev.forecast ? (
                          <div className="text-right"><div className="font-data text-[9px] uppercase tracking-widest text-slate-600">Forecast</div><div className="font-data text-xs text-brand">{ev.forecast}</div></div>
                        ) : null}
                        {ev.previous ? (
                          <div className="text-right"><div className="font-data text-[9px] uppercase tracking-widest text-slate-600">Previous</div><div className="font-data text-xs text-slate-300">{ev.previous}</div></div>
                        ) : null}
                      </div>
                      <span className={`font-data text-[9px] uppercase tracking-widest px-2 py-0.5 rounded border shrink-0 ${m.bg} ${m.c}`}>{m.label}</span>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="font-data text-[10px] text-slate-600 mt-6">Times shown in your local timezone · data refreshes every 30 min.</p>
    </div>
  );
};
